import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/ui/SEO';
import useBrandName from '../../hooks/useBrandName';
import { Award, BadgeCheck, PackageCheck, PenTool, Recycle, Scissors, SwatchBook, Wrench } from 'lucide-react';

/* ── 애니메이션 훅 (스크롤 인뷰) ── */
function useInView(threshold = 0.15) {
  const ref  = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

/* ── 섹션 래퍼 (페이드인) ── */
function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, visible } = useInView();
  return (
    <div ref={ref} className={className}
      style={{
        opacity:    visible ? 1 : 0,
        transform:  visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}>
      {children}
    </div>
  );
}

/* ── 파트너사 데이터 ── */
const PARTNERS = [
  {
    code: 'A', role: '디자인·기획', color: 'from-amber-500 to-amber-700',
    title: '트렌드 디자인',
    desc: '국내외 최신 패션 트렌드를 분석하고, 시즌별 컬렉션을 기획·디자인합니다. 감각적인 심미성과 실용성을 동시에 추구합니다.',
    points: ['시즌 컬렉션 기획', '제품 디자인 & 패턴', '브랜드 아이덴티티 관리'],
    image: '/partner_A.webp',
  },
  {
    code: 'B', role: '원자재 조달', color: 'from-stone-500 to-stone-700',
    title: '프리미엄 소재',
    desc: '국내외 최고 품질의 가죽, 원단, 부자재를 엄선 소싱합니다. 지속 가능한 소재와 친환경 소재 사용을 지향합니다.',
    points: ['천연·인조 가죽 소싱', '고급 원단·부자재 조달', '품질 인증 관리'],
    image: '/partner_B.webp',
  },
  {
    code: 'C', role: '제작·생산', color: 'from-stone-700 to-stone-900',
    title: '전문 생산',
    desc: '수십 년 경력의 장인이 참여하는 정밀 봉제와 체계적인 품질 관리로 최고의 완성도를 구현합니다.',
    points: ['정밀 봉제 & 마감', '단계별 품질 검수', 'OEM/ODM 생산'],
    image: '/partner_C.webp',
  },
  {
    code: 'D', role: '유통·판매', color: 'from-amber-700 to-stone-800',
    title: '전문 유통',
    desc: '온·오프라인 통합 유통망을 통해 소비자와 가장 가까운 접점에서 브랜드 가치를 전달합니다.',
    points: ['온라인 채널 운영', '오프라인 유통 관리', '해외 수출 & B2B'],
    image: '/partner_D.webp',
  },
];

/* ── 가치 데이터 ── */
const VALUES = [
  { image: '/value_1.webp', title: '전문성',  desc: '각 분야 최고 전문가들이 협력하여 타협 없는 품질을 추구합니다.' },
  { image: '/value_2.webp', title: '신뢰',    desc: '파트너사 간 투명한 소통과 신뢰를 바탕으로 장기적 관계를 구축합니다.' },
  { image: '/value_3.webp', title: '혁신',    desc: '전통적 장인 기술과 현대적 공정을 결합해 끊임없이 혁신합니다.' },
  { image: '/value_4.webp', title: '지속가능', desc: '환경을 생각하는 소재 선택과 생산 방식으로 미래를 준비합니다.' },
];

/* ── 연혁 ── */
const TIMELINE = [
  { year: '2023', event: '4개 전문 파트너사 업무 협약 체결' },
  { year: '2024', event: '공동 브랜드 "COBRAND" 출범 및 첫 컬렉션 출시' },
  { year: '2025', event: '온라인 판매 채널 확장 · OEM/ODM 수주 본격화' },
  { year: '2026', event: '전문 브랜드 관리 시스템 구축 · 해외 진출 준비' },
];

/* ══════════════════════════════════════════ */
export default function BrandPage() {
  const brandName = useBrandName();

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <SEO
        title="브랜드 소개"
        path="/brand"
        description={`${brandName} 브랜드 스토리, 전문 파트너사 소개, 핵심 가치와 제작 프로세스를 소개합니다.`}
        keywords={`${brandName}브랜드소개,가방브랜드,전문파트너`}
      />

      {/* ── 히어로 ── */}
      <section className="relative text-white min-h-[70vh] flex items-center overflow-hidden bg-stone-900">
        {/* 배경 이미지 */}
        <div className="absolute inset-0">
          <img src="/brand_hero.webp" alt="브랜드 히어로 배경" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-stone-950/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-28 z-10">
          <FadeIn>
            <p className="text-amber-400 tracking-[0.4em] text-xs uppercase mb-6 font-bold">
              About {brandName}
            </p>
            <h1 className="text-5xl md:text-7xl font-extrabold leading-tight mb-8">
              전문가들이<br />
              <span style={{
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b, #fbbf24)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                함께 만드는
              </span><br />
              브랜드
            </h1>
            <p className="text-stone-300 text-lg max-w-xl leading-relaxed font-normal">
              디자인·소재·생산·유통 각 분야 최고 전문가들이<br />
              하나의 브랜드로 모였습니다.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── 브랜드 스토리 ── */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <p className="text-amber-500 text-xs tracking-widest uppercase mb-4">Our Story</p>
            <h2 className="text-4xl font-bold text-stone-900 mb-6 leading-tight">
              최고의 가방을 만들기 위한<br />
              <span className="text-amber-600">약속</span>
            </h2>
            <div className="space-y-4 text-stone-600 leading-relaxed">
              <p>
                {brandName}는 "최고의 가방을 만들기 위해서는 최고의 전문가가 협력해야 한다"는 신념에서 시작했습니다.
                디자인, 소재, 생산, 유통 — 가방 제작의 모든 과정에서 타협 없는 전문성을 추구합니다.
              </p>
              <p>
                4개 파트너사가 각자의 전문 분야에서 최선을 다하고, 서로의 강점을 결합함으로써
                단일 기업이 이룰 수 없는 높은 수준의 품질과 서비스를 실현합니다.
              </p>
              <p>
                우리가 만드는 모든 가방에는 장인의 손길과 데이터 기반의 트렌드 분석, 그리고 지속 가능한 소재에 대한 고집이 담겨 있습니다.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Card 1: 2칸 차지하는 넓은 카드 (동맹의 시너지) */}
              <div className="sm:col-span-2 bg-gradient-to-br from-stone-900 to-stone-950 text-white rounded-3xl p-8 shadow-md border border-stone-800 space-y-4 hover:shadow-lg transition duration-300">
                <span className="text-amber-400 text-xs font-bold uppercase tracking-wider block">Co-Creation Synergy</span>
                <h3 className="text-xl font-extrabold text-white">경계를 허무는 4개의 전문 동맹</h3>
                <p className="text-stone-400 text-sm leading-relaxed">
                  디자인 기획, 원자재 조달, 전문 생산, 온오프라인 유통 채널이 하나의 밸류체인으로 긴밀하게 작동합니다. 단일 제조사나 브랜드가 할 수 없는 혁신적인 속도와 압도적인 품질 관리가 이 동맹에서 실현됩니다.
                </p>
              </div>

              {/* Card 2: 1칸 카드 (디테일의 미학) */}
              <div className="bg-amber-50 text-stone-900 rounded-3xl p-8 shadow-sm border border-amber-100 space-y-4 hover:shadow-md hover:bg-amber-100/30 transition duration-300">
                <span className="text-amber-700 text-xs font-bold uppercase tracking-wider block">Detail Obsession</span>
                <h3 className="text-lg font-bold text-stone-900">한 땀의 오차도 허용하지 않는 완벽함</h3>
                <p className="text-stone-600 text-xs leading-relaxed">
                  원단의 직조 상태부터 가죽의 모서리 엣지코트 마감, 황동 지퍼의 슬라이딩 감도까지 수십 번의 전수 조사를 거쳐 출고됩니다.
                </p>
              </div>

              {/* Card 3: 1칸 카드 (시간을 이기는 제품) */}
              <div className="bg-stone-50 text-stone-900 rounded-3xl p-8 shadow-sm border border-stone-200/50 space-y-4 hover:shadow-md hover:bg-stone-100 transition duration-300">
                <span className="text-stone-500 text-xs font-bold uppercase tracking-wider block">Timeless Value</span>
                <h3 className="text-lg font-bold text-stone-900">대를 이어 물려주는 라이프타임 케어</h3>
                <p className="text-stone-600 text-xs leading-relaxed">
                  우리는 유행을 타지 않는 타임리스 디자인을 지향하며, 시간이 지날수록 멋이 드는 천연 가죽만을 사용해 수선 등 평생 동반 케어를 약속합니다.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── 비전 & 미션 ── */}
      <section className="bg-stone-100 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white border border-stone-200 rounded-2xl p-8">
                <p className="text-amber-700 text-xs tracking-widest uppercase mb-3">Vision</p>
                <h3 className="text-2xl font-bold text-black mb-4">전문 브랜드로<br />업계를 선도한다</h3>
                <p className="text-stone-800 leading-relaxed text-sm">
                  국내 가방 시장에서 전문성과 품질로 신뢰받는 브랜드가 되고, 나아가 아시아 시장으로 진출하여
                  한국 가방 제조 기술의 우수성을 세계에 알립니다.
                </p>
              </div>
              <div className="bg-white border border-stone-200 rounded-2xl p-8">
                <p className="text-amber-700 text-xs tracking-widest uppercase mb-3">Mission</p>
                <h3 className="text-2xl font-bold text-black mb-4">협력으로<br />가치를 창출한다</h3>
                <p className="text-stone-800 leading-relaxed text-sm">
                  파트너사 간 신뢰와 투명한 협력을 통해 소비자에게는 최고의 제품을, 파트너사에게는
                  지속 가능한 성장을 제공합니다.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── 파트너사 소개 ── */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-amber-500 text-xs tracking-widest uppercase mb-3">Professional Alliance</p>
              <h2 className="text-4xl font-bold text-stone-900 mb-4">전문 파트너사</h2>
              <p className="text-stone-600 max-w-xl mx-auto">각 분야 최고 전문가들이 만든 완벽한 협력 체계</p>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PARTNERS.map((p, i) => (
              <FadeIn key={p.code} delay={i * 0.1}>
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition group">
                  <div className={`h-2 bg-gradient-to-r ${p.color}`} />
                  <div className="p-8">
                    <div className="flex items-start gap-5 mb-6">
                      <div className="w-16 h-16 rounded-2xl overflow-hidden bg-stone-100 flex-shrink-0 border border-stone-200">
                        <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="text-xs text-stone-600 font-semibold tracking-wide uppercase mb-1">{p.role}</p>
                        <h3 className="text-xl font-bold text-stone-900">{p.title}</h3>
                      </div>
                    </div>
                    <p className="text-stone-600 text-sm leading-relaxed mb-5">{p.desc}</p>
                    <ul className="space-y-2">
                      {p.points.map((pt, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm text-stone-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── 핵심 가치 ── */}
      <section className="py-24 bg-white text-stone-900">
        <div className="max-w-7xl mx-auto px-6">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-amber-700 text-xs tracking-widest uppercase mb-3">Core Values</p>
              <h2 className="text-4xl font-bold mb-4">우리의 핵심 가치</h2>
              <p className="text-stone-600 max-w-xl mx-auto text-sm">모든 결정과 행동의 기준이 되는 네 가지 가치</p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <FadeIn key={v.title} delay={i * 0.1}>
                <div className="bg-stone-50 rounded-2xl overflow-hidden hover:bg-stone-100/80 hover:shadow-lg transition text-center group flex flex-col border border-stone-200/40">
                  <div className="h-44 overflow-hidden relative">
                    <img src={v.image} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col justify-center">
                    <h3 className="text-lg font-bold mb-3 text-amber-700">{v.title}</h3>
                    <p className="text-stone-600 text-sm leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── 제작 프로세스 ── */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <FadeIn>
          <div className="text-center mb-16">
            <p className="text-amber-500 text-xs tracking-widest uppercase mb-3">Manufacturing Process</p>
            <h2 className="text-4xl font-bold text-stone-900 mb-4">전문 제작 프로세스</h2>
            <p className="text-stone-500 max-w-xl mx-auto text-sm">기획부터 고객 전달까지, 모든 단계에서 전문성을 발휘합니다</p>
          </div>
        </FadeIn>

        <div className="relative">
          {/* 연결선 */}
          <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200" />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { step: '01', icon: PenTool, title: '디자인', desc: '트렌드 분석\n컬렉션 기획' },
              { step: '02', icon: SwatchBook, title: '소재 선택', desc: '원단·부자재\n품질 검증' },
              { step: '03', icon: Scissors, title: '재단·봉제', desc: '정밀 재단\n전문가 봉제' },
              { step: '04', icon: BadgeCheck, title: '품질 검수', desc: '전 제품\n단계별 검사' },
              { step: '05', icon: PackageCheck, title: '출고·유통', desc: '포장·배송\n채널 관리' },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
              <FadeIn key={s.step} delay={i * 0.08}>
                <div className="text-center relative">
                  <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center text-3xl mx-auto mb-4 shadow-lg relative z-10">
                    <Icon size={26} aria-hidden="true" />
                  </div>
                  <span className="text-xs font-bold text-amber-600 tracking-widest">STEP {s.step}</span>
                  <h3 className="font-bold text-stone-900 mt-1 mb-2">{s.title}</h3>
                  <p className="text-stone-500 text-xs leading-relaxed whitespace-pre-line">{s.desc}</p>
                </div>
              </FadeIn>
            )})}
          </div>
        </div>
      </section>

      {/* ── 연혁 ── */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-4xl mx-auto px-6">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-amber-500 text-xs tracking-widest uppercase mb-3">History</p>
              <h2 className="text-4xl font-bold text-stone-900">브랜드 연혁</h2>
            </div>
          </FadeIn>

          <div className="relative">
            {/* 세로 연결선 (PC에서는 중앙, 모바일에서는 좌측) */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-stone-200 -translate-x-1/2" />

            <div className="space-y-12">
              {TIMELINE.map((t, i) => {
                const eventText = t.event.replace('COBRAND', brandName);
                return (
                  <FadeIn key={t.year} delay={i * 0.1}>
                    {/* PC: alternating row/row-reverse, Mobile: always row with left alignment */}
                    <div className={`flex items-center gap-6 md:gap-8 flex-row ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} relative`}>
                      
                      {/* 카드 내용 (PC: alternating text align, Mobile: always left align) */}
                      <div className={`flex-1 text-left ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0`}>
                        <div className={`inline-block bg-white rounded-2xl p-6 shadow-sm border border-stone-100 w-full max-w-md ${i % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'}`}>
                          <p className="text-amber-600 font-bold text-lg mb-1">{t.year}</p>
                          <p className="text-stone-700 text-sm leading-relaxed">{eventText}</p>
                        </div>
                      </div>
                      
                      {/* 타임라인 원형 포인트 (PC: 중앙 고정, Mobile: 좌측 선 위에 고정) */}
                      <div className="absolute left-4 md:relative md:left-auto md:translate-x-0 w-4 h-4 rounded-full bg-amber-500 flex-shrink-0 shadow-md z-10 border-2 border-white -translate-x-1/2" />
                      
                      {/* 빈 영역 (PC에서만 필요, 모바일에서는 숨김) */}
                      <div className="hidden md:block flex-1" />
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── 품질 약속 ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <FadeIn>
            <div className="bg-white rounded-3xl p-12 text-stone-900 text-center">
              <p className="text-amber-700 text-xs tracking-widest uppercase mb-4">Quality Promise</p>
              <h2 className="text-3xl font-bold mb-6">우리의 품질 약속</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
                {[
                  { icon: Award, title: '전 제품 품질 보증', desc: '모든 제품은 엄격한 품질 검수를 통과합니다' },
                  { icon: Recycle, title: '지속 가능한 소재', desc: '환경을 생각한 친환경 소재 사용을 추구합니다' },
                  { icon: Wrench, title: 'A/S 전문 지원', desc: '구매 후에도 전문가의 케어 서비스를 제공합니다' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                  <div key={i} className="bg-white/5 rounded-2xl p-6">
                    <Icon size={30} className="mx-auto mb-4 text-stone-700" aria-hidden="true" />
                    <h3 className="font-bold text-amber-700 mb-2">{item.title}</h3>
                    <p className="text-stone-600 text-sm">{item.desc}</p>
                  </div>
                )})}
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/catalog"
                  className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 py-3 rounded-full transition">
                  제품 보러가기
                </Link>
                <Link to="/oem"
                  className="border border-white/30 hover:border-amber-400 hover:text-amber-400 text-stone-900 font-semibold px-8 py-3 rounded-full transition">
                  전문 제작 문의
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

    </div>
  );
}
